/**
 * In-Game Idle Manager
 *
 * Detects player inactivity during active gameplay and manages
 * the AFK countdown, vibration feedback, and idle state transitions.
 *
 * This is player-level state (not a coordinator phase). The coordinator
 * owns the instance and wires callbacks to update QuickstartState.
 */

export interface InGameIdleManagerConfig {
  /** Idle timeout duration in seconds (0 = disabled) */
  timeoutSeconds: number;

  /** Called when idle state begins (no input for the full timeout) */
  onIdleStart: () => void;

  /** Called when idle state ends (player resumed input) */
  onIdleEnd: () => void;

  /** Called when the AFK countdown reaches zero */
  onIdleTimeout: () => void;
}

const FORCE_IDLE_FALLBACK_SECONDS = 60;

export class InGameIdleManager {
  private config: InGameIdleManagerConfig;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private idleStartTime?: number;
  private vibrationInterval?: ReturnType<typeof setInterval>;
  private isIdle = false;
  private isActive = false;
  /** Active countdown duration — may differ from config when forceIdle uses fallback */
  private effectiveTimeout = 0;

  constructor(config: InGameIdleManagerConfig) {
    this.config = config;
  }

  /**
   * Begin monitoring for idle. Call when entering playing_active.
   */
  public start(): void {
    if (this.config.timeoutSeconds <= 0) {
      console.warn('[InGameIdleManager] Idle detection DISABLED (timeoutSeconds:', this.config.timeoutSeconds, ')');
      return;
    }
    this.isActive = true;
    this.effectiveTimeout = this.config.timeoutSeconds;
    this.scheduleIdleCheck();
    console.log(`[InGameIdleManager] Started monitoring (${this.effectiveTimeout}s timeout)`);
  }

  /**
   * Stop monitoring entirely. Call when leaving playing_active.
   */
  public stop(): void {
    this.isActive = false;
    this.clearIdleTimer();
    if (this.isIdle) {
      this.exitIdle();
    }
    console.log('[InGameIdleManager] Stopped monitoring');
  }

  /**
   * Reset the idle timer. Call on every user input event.
   */
  public resetIdleTimer(): void {
    if (!this.isActive) return;

    if (this.isIdle) {
      this.exitIdle();
    }

    this.scheduleIdleCheck();
  }

  /**
   * Get remaining seconds on the AFK countdown.
   * Returns 0 when not idle or when the manager is disabled.
   */
  public getRemainingTime(): number {
    if (!this.isIdle || !this.idleStartTime) return 0;
    const elapsed = (Date.now() - this.idleStartTime) / 1000;
    return Math.max(0, Math.ceil(this.effectiveTimeout - elapsed));
  }

  /** Whether the player is currently in the idle state */
  public getIsIdle(): boolean {
    return this.isIdle;
  }

  /** Whether idle monitoring is enabled (timeoutSeconds > 0) */
  public isEnabled(): boolean {
    return this.config.timeoutSeconds > 0;
  }

  /** The timeout duration currently in effect (may differ from config during forceIdle) */
  public getEffectiveTimeout(): number {
    return this.effectiveTimeout;
  }

  /**
   * Force enter idle state (for debug/testing)
   */
  public forceIdle(): void {
    if (!this.isActive) {
      this.isActive = true;
    }
    this.effectiveTimeout = this.config.timeoutSeconds || FORCE_IDLE_FALLBACK_SECONDS;
    this.clearIdleTimer();
    this.enterIdle();
  }

  public cleanup(): void {
    this.stop();
  }

  // ---------------------------------------------------------------------------

  private scheduleIdleCheck(): void {
    if (this.effectiveTimeout <= 0) return;
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.enterIdle();
    }, this.effectiveTimeout * 1000);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
    }
  }

  private enterIdle(): void {
    if (this.isIdle) return;

    this.isIdle = true;
    this.idleStartTime = Date.now();
    console.log('[InGameIdleManager] Player idle — AFK countdown started');

    this.config.onIdleStart();
    this.startVibration();

    // Schedule the final timeout (countdown reaching zero)
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      console.log('[InGameIdleManager] AFK countdown expired');
      this.stopVibration();
      this.config.onIdleTimeout();
    }, this.effectiveTimeout * 1000);
  }

  private exitIdle(): void {
    if (!this.isIdle) return;

    this.isIdle = false;
    this.idleStartTime = undefined;
    this.effectiveTimeout = this.config.timeoutSeconds;
    this.clearIdleTimer();
    this.stopVibration();
    console.log('[InGameIdleManager] Player resumed — AFK dismissed');

    this.config.onIdleEnd();
  }

  // ---------------------------------------------------------------------------
  // Vibration (ramp-up / ramp-down pattern synced with CSS pulse)
  // ---------------------------------------------------------------------------

  private startVibration(): void {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;

    const pattern = [100, 50, 200, 50, 300, 200, 300, 50, 200, 50, 100];
    const patternDuration = pattern.reduce((a, b) => a + b, 0);

    try {
      navigator.vibrate(pattern);
    } catch { /* best effort */ }

    this.vibrationInterval = setInterval(() => {
      try {
        navigator.vibrate(pattern);
      } catch { /* best effort */ }
    }, patternDuration + 400);
  }

  private stopVibration(): void {
    if (this.vibrationInterval) {
      clearInterval(this.vibrationInterval);
      this.vibrationInterval = undefined;
    }

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(0);
      } catch { /* best effort */ }
    }
  }
}
